import React from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  FormLabel,
  Typography,
} from "@mui/material";
import { Shield, ShieldAlert } from "lucide-react";
import { useTranslate } from "@refinedev/core";

export type ZoneType = "AUTHORIZED" | "FORBIDDEN";

interface ZoneTypeSelectorProps {
  value: ZoneType;
  onChange: (value: ZoneType) => void;
  disabled?: boolean;
}

/**
 * ZoneTypeSelector - Toggle between AUTHORIZED and FORBIDDEN zone types
 *
 * AUTHORIZED: Default behavior - notifications sent when user enters/exits
 * FORBIDDEN: Alert behavior - alerts sent when user enters the zone
 */
const ZoneTypeSelector: React.FC<ZoneTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const translate = useTranslate();

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: ZoneType | null
  ) => {
    // Prevent deselection - always keep one selected
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <FormLabel sx={{ mb: 1, display: "block" }}>
        {translate("sharedPlaces.fields.zoneType")}
      </FormLabel>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        aria-label="zone type"
        disabled={disabled}
        fullWidth
        sx={{
          "& .MuiToggleButton-root": {
            py: 1.5,
          },
        }}
      >
        <ToggleButton
          value="AUTHORIZED"
          aria-label="authorized zone"
          sx={{
            "&.Mui-selected": {
              backgroundColor: "success.light",
              color: "success.contrastText",
              "&:hover": {
                backgroundColor: "success.main",
              },
            },
          }}
        >
          <Shield size={20} style={{ marginRight: 8 }} />
          <Box sx={{ textAlign: "left" }}>
            <Typography variant="body2" fontWeight="bold">
              {translate("sharedPlaces.zoneTypes.authorized")}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
              {translate("sharedPlaces.zoneTypes.authorizedDescription")}
            </Typography>
          </Box>
        </ToggleButton>
        <ToggleButton
          value="FORBIDDEN"
          aria-label="forbidden zone"
          sx={{
            "&.Mui-selected": {
              backgroundColor: "error.light",
              color: "error.contrastText",
              "&:hover": {
                backgroundColor: "error.main",
              },
            },
          }}
        >
          <ShieldAlert size={20} style={{ marginRight: 8 }} />
          <Box sx={{ textAlign: "left" }}>
            <Typography variant="body2" fontWeight="bold">
              {translate("sharedPlaces.zoneTypes.forbidden")}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
              {translate("sharedPlaces.zoneTypes.forbiddenDescription")}
            </Typography>
          </Box>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default ZoneTypeSelector;
